import React, { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { taskService } from '@/services/taskService'
import { planService } from '@/services/planService'
import { useToast } from '@/contexts/ToastContext'
import { Plan } from '@/types/plan'

interface LinkableTask {
  id: string
  title: string
  progressPercentage: number
}

interface LinkTaskToGoalModalProps {
  planId: string
  goalId: string
  relationshipId: string
  alreadyLinkedTaskIds: string[]
  onClose: () => void
  onLinked: (plan: Plan) => void
}

const LinkTaskToGoalModal: React.FC<LinkTaskToGoalModalProps> = ({
  planId,
  goalId,
  relationshipId,
  alreadyLinkedTaskIds,
  onClose,
  onLinked
}) => {
  const { showToast } = useToast()
  const [tasks, setTasks] = useState<LinkableTask[]>([])
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    let cancelled = false
    taskService
      .getTasksForRelationship(relationshipId)
      .then((data) => {
        if (cancelled) return
        const linkable = (Array.isArray(data) ? data : []).filter(
          (task: LinkableTask) => !alreadyLinkedTaskIds.includes(task.id)
        )
        setTasks(linkable as LinkableTask[])
      })
      .catch(() => {
        showToast({ type: 'error', message: 'Failed to load tasks' })
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relationshipId])

  const toggle = (taskId: string) => {
    setSelectedIds((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
    )
  }

  const handleLink = async () => {
    try {
      setIsSubmitting(true)
      let updated: Plan | null = null
      for (const taskId of selectedIds) {
        updated = await planService.linkTask(planId, goalId, taskId)
      }
      showToast({ type: 'success', message: 'Tasks linked' })
      if (updated) {
        onLinked(updated)
      }
      onClose()
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to link tasks'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-xl font-semibold text-gray-900">Link Tasks to Goal</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 max-h-80 overflow-y-auto">
          {loading && <p className="text-gray-600 text-sm">Loading tasks…</p>}

          {!loading && tasks.length === 0 && (
            <p className="text-gray-600 text-sm">
              No linkable tasks. All tasks in this relationship are already linked to this goal, or
              no tasks exist yet.
            </p>
          )}

          {!loading && tasks.length > 0 && (
            <ul className="space-y-2">
              {tasks.map((task) => (
                <li key={task.id}>
                  <label className="flex items-center gap-3 p-2 border rounded-md hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(task.id)}
                      onChange={() => toggle(task.id)}
                      aria-label={task.title}
                    />
                    <span className="flex-1 text-sm text-gray-900">{task.title}</span>
                    <span className="text-xs text-gray-500">{task.progressPercentage}%</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t px-6 py-4">
          <Button variant="outline" size="sm" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleLink} disabled={isSubmitting || selectedIds.length === 0}>
            {isSubmitting ? 'Linking…' : `Link Selected (${selectedIds.length})`}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default LinkTaskToGoalModal
